import React from "react";

import { NOTI_ITEMS } from "./constants";

/* === Live stats ===
 * Mỗi chỉ số có nhịp cập nhật riêng:
 * - Jackpot: neo theo thời gian thực, chỉ tăng, tự "nổ hũ" khi hết chu kỳ.
 * - Người thắng / nhận code: đổi tên + giải thưởng theo chu kỳ ngẫu nhiên thưa.
 * - Đang chơi: dao động quanh mức nền theo giờ trong ngày, mỗi lần ±0.5-1.5%.
 * - Đã chi thưởng hôm nay: suy ra từ thời gian đã trôi trong ngày (tự reset
 *   qua nửa đêm về PAID_BASE), chỉ tăng, nhịp cập nhật không đều.
 */
const JACKPOT_TICK_MS = 3_000;
const WINNER_TICK_MS = 12_000;
const CODE_TICK_MS = 25_000;
const ONLINE_TICK_MS = 7_000;
/* Card chi thưởng: chia thời gian thành các slot, mỗi slot "chốt số" một lần
 * tại một thời điểm lệch ngẫu nhiên (nhưng tất định theo slot) trong slot đó */
const PAID_SLOT_MS = 55_000;
/* Độ lệch tối đa của mốc chốt trong slot (tỉ lệ theo slot):
 * 0.3 → khoảng cách giữa 2 lần nhảy dao động ~38-72s quanh mức 55s */
const PAID_SLOT_JITTER = 0.3;

/* Tổng chi thưởng cộng dồn trong ngày (đạt thêm được lúc 24h) */
const PAID_PER_DAY = 15_000_000_000;
/* Mức khởi điểm lúc 0h mỗi ngày — không về 0 để không vênh với card "vừa thắng" */
const PAID_BASE = 300_000_000;

export const formatWin = (value: number) =>
  `${value.toLocaleString("vi-VN")} WIN`;

export type LiveStats = {
  jackpot: number;
  winnerName: string;
  winnerPrize: number;
  codeName: string;
  codePrize: number;
  online: number;
  paidToday: number;
};

const randBetween = (min: number, max: number) =>
  Math.floor(min + Math.random() * (max - min));

/* Random tất định theo seed (0..1) — cùng seed luôn ra cùng kết quả,
 * dùng để số liệu neo theo thời gian không đổi khi reload */
const seededRandom = (seed: number) => {
  let t = (seed + 0x6d2b79f5) | 0;
  t = Math.imul(t ^ (t >>> 15), t | 1);
  t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
};

/* Bốc tên ngẫu nhiên, loại tên đang hiển thị ở card kia để 2 card không trùng nhau */
const pickRandomNoti = (excludeName?: string) => {
  const pool = NOTI_ITEMS.filter((item) => item.username !== excludeName);
  return pool[Math.floor(Math.random() * pool.length)];
};

/* === Jackpot neo theo thời gian ===
 * Jackpot là hàm của thời gian thực nên F5 không bị tụt số; tăng đều từ
 * JACKPOT_BASE, chạm JACKPOT_CAP thì coi như có người nổ hũ và quay về BASE.
 */
const JACKPOT_BASE = 1_200_000_000;
const JACKPOT_CAP = 1_500_000_000;
const JACKPOT_CYCLE_MS = 4 * 60 * 60 * 1000; // ~4 tiếng một chu kỳ nổ hũ
const JACKPOT_EPOCH = new Date("2026-01-01T00:00:00").getTime();

const jackpotAt = (now: Date) => {
  // Lượng tử hoá theo mốc tick tuyệt đối: giá trị chỉ đổi tại các mốc này,
  // reload giữa 2 mốc vẫn ra đúng con số đang hiển thị
  const elapsed =
    Math.floor((now.getTime() - JACKPOT_EPOCH) / JACKPOT_TICK_MS) *
    JACKPOT_TICK_MS;
  const cycleProgress = (elapsed % JACKPOT_CYCLE_MS) / JACKPOT_CYCLE_MS;
  const value = JACKPOT_BASE + cycleProgress * (JACKPOT_CAP - JACKPOT_BASE);
  return Math.round(value / 1_000) * 1_000;
};

/* Giải thưởng trông "thật": bội số của 50k, thiên về giải nhỏ */
const randomPrize = (min: number, max: number) => {
  const skewed = min + (max - min) * Math.pow(Math.random(), 2);
  return Math.round(skewed / 50_000) * 50_000;
};

/* Mức người chơi nền theo giờ: thấp lúc rạng sáng, đỉnh buổi tối */
const onlineBaseByHour = (hour: number) => {
  if (hour < 6) return 8_500; // 0h-6h: vắng
  if (hour < 11) return 10_500; // sáng
  if (hour < 14) return 12_500; // trưa
  if (hour < 18) return 11_500; // chiều
  if (hour < 23) return 14_500; // tối: cao điểm
  return 11_000; // khuya
};

/* Đã chi thưởng = mức khởi điểm + tiến độ ngày × tổng ngày.
 * Tính từ 0h nên qua ngày mới tự reset về PAID_BASE. */
const paidTodayAt = (timestamp: number, startOfDayMs: number) => {
  const dayProgress = (timestamp - startOfDayMs) / 86_400_000;
  const paid = PAID_BASE + dayProgress * PAID_PER_DAY;
  return Math.round(paid / 10_000) * 10_000;
};

/* Giá trị hiển thị: mỗi slot 18s chốt số một lần tại thời điểm lệch ngẫu nhiên
 * (tất định theo slot) trong slot — nhịp cập nhật trông tự nhiên nhưng reload
 * tại bất kỳ lúc nào cũng ra đúng con số đang hiển thị */
const paidTodayDisplayAt = (now: Date) => {
  const startOfDay = new Date(now);
  startOfDay.setHours(0, 0, 0, 0);
  const startOfDayMs = startOfDay.getTime();

  const slot = Math.floor(now.getTime() / PAID_SLOT_MS);
  const updateAtOf = (s: number) =>
    s * PAID_SLOT_MS + seededRandom(s) * PAID_SLOT_MS * PAID_SLOT_JITTER;

  // Mốc chốt số gần nhất đã trôi qua (của slot này hoặc slot trước)
  const anchor =
    now.getTime() >= updateAtOf(slot) ? updateAtOf(slot) : updateAtOf(slot - 1);
  return paidTodayAt(Math.max(anchor, startOfDayMs), startOfDayMs);
};

const createInitialStats = (): LiveStats => {
  const now = new Date();
  const winnerName = pickRandomNoti().username;
  return {
    jackpot: jackpotAt(now),
    winnerName,
    winnerPrize: randomPrize(2_000_000, 120_000_000),
    codeName: pickRandomNoti(winnerName).username,
    codePrize: randomPrize(500_000, 20_000_000),
    online: onlineBaseByHour(now.getHours()) + randBetween(-400, 400),
    paidToday: paidTodayDisplayAt(now),
  };
};

export const useLiveStats = (): LiveStats => {
  const [liveStats, setLiveStats] =
    React.useState<LiveStats>(createInitialStats);

  /* Cập nhật các card thống kê, mỗi chỉ số một nhịp riêng */
  React.useEffect(() => {
    // Jackpot: tính lại theo thời gian thực (tự "nổ hũ" khi hết chu kỳ)
    const jackpotTimer = window.setInterval(() => {
      setLiveStats((prev) => ({
        ...prev,
        jackpot: jackpotAt(new Date()),
      }));
    }, JACKPOT_TICK_MS);

    // Người thắng mới: ~50% mỗi 12s (trung bình ~24s/người)
    const winnerTimer = window.setInterval(() => {
      if (Math.random() < 0.5) {
        setLiveStats((prev) => ({
          ...prev,
          winnerName: pickRandomNoti(prev.codeName).username,
          winnerPrize: randomPrize(2_000_000, 120_000_000),
        }));
      }
    }, WINNER_TICK_MS);

    // Người nhận code mới: ~40% mỗi 25s
    const codeTimer = window.setInterval(() => {
      if (Math.random() < 0.4) {
        setLiveStats((prev) => ({
          ...prev,
          codeName: pickRandomNoti(prev.winnerName).username,
          codePrize: randomPrize(500_000, 20_000_000),
        }));
      }
    }, CODE_TICK_MS);

    // Đang chơi: kéo dần về mức nền theo giờ + nhiễu ±0.5-1.5% mỗi tick
    const onlineTimer = window.setInterval(() => {
      setLiveStats((prev) => {
        const base = onlineBaseByHour(new Date().getHours());
        const pullToBase = (base - prev.online) * 0.1;
        const amplitude = prev.online * (0.005 + Math.random() * 0.01);
        const noise = (Math.random() < 0.5 ? -1 : 1) * amplitude;
        return {
          ...prev,
          online: Math.round(prev.online + pullToBase + noise),
        };
      });
    }, ONLINE_TICK_MS);

    // Đã chi thưởng: poll mỗi 2s nhưng giá trị chỉ đổi khi qua mốc chốt số
    // của slot (xem paidTodayDisplayAt) — reload không làm nhảy số
    const paidTimer = window.setInterval(() => {
      setLiveStats((prev) => ({
        ...prev,
        paidToday: paidTodayDisplayAt(new Date()),
      }));
    }, 2_000);

    return () => {
      window.clearInterval(jackpotTimer);
      window.clearInterval(winnerTimer);
      window.clearInterval(codeTimer);
      window.clearInterval(onlineTimer);
      window.clearInterval(paidTimer);
    };
  }, []);

  return liveStats;
};
